(() => {
    'use strict';
    const qs = (sel, root = document) => root.querySelector(sel);
    const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
    const markBound = (el, key) => {
        if (!el)
            return false;
        const attr = `data-tc-bound-${key}`;
        if (el.hasAttribute(attr))
            return false;
        el.setAttribute(attr, 'true');
        return true;
    };
    const rafThrottle = (fn) => {
        let raf = 0;
        return (...args) => {
            if (raf)
                return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                fn(...args);
            });
        };
    };
    const _normRe = (() => {
        try {
            return /[^\p{L}\p{N}]+/gu;
        }
        catch (e) {
            return /[^a-z0-9]+/g;
        }
    })();
    const normalize = (s) => {
        const str = (s ?? '').toString().toLowerCase();
        try {
            return str.normalize('NFKD').replace(_normRe, '');
        }
        catch (e) {
            return str.replace(_normRe, '');
        }
    };
    const stripHtml = (html) => (html ?? '').toString().replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const INDEX_KEY = 'tcProductIndex.v1';
    const INDEX_TTL_MS = 12 * 60 * 60 * 1000;
    let indexPromise = null;
    const fetchJson = (url) => fetch(url, { credentials: 'same-origin' }).then((r) => {
        if (!r.ok)
            throw new Error(`HTTP ${r.status}`);
        return r.json();
    });
    const fetchAllProducts = async () => {
        const out = [];
        const maxPages = 20;
        for (let page = 1; page <= maxPages; page++) {
            const data = await fetchJson(`/products.json?limit=250&page=${page}`);
            const products = (data && data.products) || [];
            if (!products.length)
                break;
            for (const p of products) {
                const title = p.title || '';
                const body = stripHtml(p.body_html || '');
                const image = (p.images && p.images[0] && (p.images[0].src || p.images[0])) || '';
                const v0 = (p.variants && p.variants[0]) || {};
                const price = v0.price || '';
                const compare = v0.compare_at_price || '';
                const url = p.handle ? `/products/${p.handle}` : (p.url || '#');
                const hay = normalize(`${title} ${body}`);
                out.push({ title, body, image, url, price, compare, _n: hay });
            }
            if (products.length < 250)
                break;
        }
        return out;
    };
    const loadProductIndex = async () => {
        if (indexPromise)
            return indexPromise;
        indexPromise = (async () => {
            try {
                const raw = localStorage.getItem(INDEX_KEY);
                if (raw) {
                    const cached = JSON.parse(raw);
                    if (cached && Array.isArray(cached.items) && cached.ts && (Date.now() - cached.ts) < INDEX_TTL_MS) {
                        return cached.items;
                    }
                }
            }
            catch (e) { }
            const items = await fetchAllProducts();
            try {
                localStorage.setItem(INDEX_KEY, JSON.stringify({ ts: Date.now(), items }));
            }
            catch (e) { }
            return items;
        })();
        return indexPromise;
    };
    const findMatches = (items, rawQuery, limit = 48) => {
        const raw = (rawQuery || '').toString().trim();
        if (!raw)
            return [];

        // If the query looks like a part/sku code, prefer strict prefix matches on the product title.
        // This avoids false positives where a similar code appears only in body/cross-reference text.
        const compact = raw.replace(/\s+/g, '');
        const looksLikePart = (() => {
            if (compact.length < 5)
                return false;
            // Must contain at least one digit
            if (!/\d/.test(compact))
                return false;
            // Only allow common SKU characters
            if (!/^[a-z0-9.\-_/]+$/i.test(compact))
                return false;
            return true;
        })();
        if (looksLikePart) {
            const qn = normalize(compact);
            const strict = [];
            for (const p of items) {
                const tn = normalize(p.title || '');
                if (tn.startsWith(qn)) {
                    strict.push(p);
                    if (strict.length >= limit)
                        break;
                }
            }
            if (strict.length)
                return strict;
        }

        const parts = raw.split(/\s+/).map(normalize).filter(Boolean);
        if (!parts.length)
            return [];
        const hits = [];
        for (const p of items) {
            const hay = p._n || '';
            let ok = true;
            for (const t of parts) {
                if (!hay.includes(t)) {
                    ok = false;
                    break;
                }
            }
            if (!ok)
                continue;
            hits.push(p);
            if (hits.length >= limit)
                break;
        }
        return hits;
    };
    const formatMoneyFromCents = (cents) => {
        const moneyFormat = document.body?.dataset?.moneyFormat || '';
        const c = Number.isFinite(Number(cents)) ? Math.round(Number(cents)) : 0;
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && moneyFormat) {
            try {
                return window.Shopify.formatMoney(c, moneyFormat);
            }
            catch (e) { }
        }
        return (c / 100).toFixed(2);
    };
    const formatMoneySafe = (value) => {
        const raw = (value ?? '').toString();
        const n = (typeof value === 'number') ? value : parseFloat(raw.replace(',', '.'));
        if (!Number.isFinite(n))
            return formatMoneyFromCents(0);
        if (Number.isInteger(n) && Math.abs(n) >= 10000 && !/[\.,]/.test(raw)) {
            return formatMoneyFromCents(n);
        }
        return formatMoneyFromCents(Math.round(n * 100));
    };
    const bindSmartSearchForms = (root) => {
        qsa('form[data-smart-search="true"]', root).forEach((form) => {
            if (!markBound(form, 'smart-search-form'))
                return;
            form.addEventListener('submit', () => {
                const q = qs('input[name="q"]', form);
                const tc = qs('input[name="tc_display"]', form);
                const val = (q && q.value ? q.value : '').toString().trim();
                if (tc)
                    tc.value = val;
                try {
                    sessionStorage.setItem('tc_search_display', val);
                }
                catch (e) { }
            });
        });
    };
    const ensureDropdown = (form) => {
        let el = qs('.tc-searchDrop', form);
        if (el)
            return el;
        el = document.createElement('div');
        el.className = 'tc-searchDrop';
        el.hidden = true;
        el.setAttribute('role', 'listbox');
        form.appendChild(el);
        return el;
    };
    const initSmartSearchDropdown = (root) => {
        qsa('form[data-smart-search="true"]', root).forEach((form) => {
            const input = qs('input[name="q"]', form);
            if (!input)
                return;
            if (!markBound(input, 'smart-search-dd'))
                return;
            const drop = ensureDropdown(form);
            const update = rafThrottle(async () => {
                const val = (input.value || '').toString().trim();
                if (val.length < 3) {
                    drop.hidden = true;
                    drop.innerHTML = '';
                    return;
                }
                let items = [];
                try {
                    items = await loadProductIndex();
                }
                catch (e) {
                    drop.hidden = true;
                    return;
                }
                const hits = findMatches(items, val, 6);
                if (!hits.length) {
                    drop.hidden = true;
                    drop.innerHTML = '';
                    return;
                }
                const hidePrices = !!window.tcHidePrices;
                const porText = (window.tcI18n && window.tcI18n.priceOnRequestTitle) ? window.tcI18n.priceOnRequestTitle : 'Prices on request';
                const html = hits.map((p) => {
                    const img = p.image ? `<span class="tc-searchDrop__thumb"><img src="${p.image}" alt="" loading="lazy"></span>` : `<span class="tc-searchDrop__thumb" aria-hidden="true"></span>`;
                    const isZero = typeof p.price === 'number' && p.price <= 0;
                    const sub = (hidePrices || isZero)
                        ? `<span class="tc-searchDrop__sub"><span class="tc-pricePill" style="display:inline-flex">${porText}</span></span>`
                        : (p.price ? `<span class="tc-searchDrop__sub">${formatMoneySafe(p.price)}${p.compare ? `  ·  <span class=\"price--compare\">${formatMoneySafe(p.compare)}</span>` : ''}</span>` : '');
                    return `<a class="tc-searchDrop__item" role="option" href="${p.url}">${img}<span class="tc-searchDrop__meta"><span class="tc-searchDrop__title">${(p.title || '').toString()}</span>${sub}</span></a>`;
                }).join('');
                drop.innerHTML = `${html}<div class="tc-searchDrop__footer">Press Enter to search all results</div>`;
                drop.hidden = false;
            });
            input.addEventListener('input', update);
            input.addEventListener('focus', update);
            document.addEventListener('click', (e) => {
                if (form.contains(e.target))
                    return;
                drop.hidden = true;
            });
        });
    };
    const initSmartSearchPage = async () => {
        if (!/\/(search)$/.test(window.location.pathname.replace(/\/+$/, '')))
            return;
        const host = qs('[data-tc-smart-results]');
        if (!host)
            return;
        const qInput = qs('input[name="q"]');
        const urlParams = new URLSearchParams(window.location.search);
        const raw = (urlParams.get('tc_display') || urlParams.get('q') || (qInput && qInput.value) || '').toString().trim();
        if (!raw)
            return;
        if (!/[0-9]/.test(raw))
            return;
        if (qInput && !qInput.value) {
            try {
                const mem = sessionStorage.getItem('tc_search_display');
                if (mem)
                    qInput.value = mem;
            }
            catch (e) { }
        }
        const native = qs('[data-tc-native-results]');
        const meta = qs('[data-tc-smart-meta]');
        const countEl = qs('[data-tc-smart-count]');
        const termsEl = qs('[data-tc-smart-terms]');
        if (termsEl)
            termsEl.textContent = raw;
        if (meta)
            meta.hidden = false;
        host.hidden = true;
        host.innerHTML = `<div class="card card__pad">${i18nStr('searching', 'Searching…')}</div>`;
        host.hidden = false;
        let items = [];
        try {
            items = await loadProductIndex();
        }
        catch (e) {
            return;
        }
        const hits = findMatches(items, raw, 96);
        if (countEl)
            countEl.textContent = `${hits.length}`;
        if (native)
            native.hidden = true;
        if (!hits.length) {
            const t = i18nStr('noMatchesTitle', 'No matches found.');
            const d = i18nStr('noMatchesText', 'Try a different part code or keyword.');
            host.innerHTML = `<div class="card card__pad"><strong>${t}</strong><div style="color:var(--color-muted);margin-top:6px">${d}</div></div>`;
            try { initReveal(host); } catch (e) { }
            return;
        }
        const hidePrices = !!window.tcHidePrices;
        host.innerHTML = hits.map((p) => {
            const por = `<span class="tc-pricePill">${(window.tcI18n && window.tcI18n.priceOnRequestTitle) ? window.tcI18n.priceOnRequestTitle : 'Prices on request'}</span>`;
            const isZero = typeof p.price === 'number' && p.price <= 0;
            const price = (hidePrices || isZero)
                ? `<div class="price">${por}</div>`
                : (p.price ? `<div class="price">${formatMoneySafe(p.price)}${p.compare ? ` <span class=\"price--compare\">${formatMoneySafe(p.compare)}</span>` : ''}</div>` : '');
            const img = p.image ? `<img class="product-card__img" src="${p.image}" alt="" loading="lazy">` : '';
            return `
        <article class="product-card">
          <a class="product-card__media" href="${p.url}" aria-label="${(p.title || '').toString().replace(/\"/g, '&quot;')}">${img}</a>
          <div class="product-card__body stack" style="gap:8px">
            <a class="product-card__title" href="${p.url}">${(p.title || '').toString()}</a>
            ${price}
          </div>
        </article>
      `;
        }).join('');
        try { initReveal(host); } catch (e) { }
    };
    const initHeaderMotion = () => {
        const header = qs('.header');
        if (!header)
            return;
        const onScroll = rafThrottle(() => {
            header.classList.toggle('is-scrolled', window.scrollY > 12);
        });
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
    };
    let toastEl = null;
    const ensureToast = () => {
        if (toastEl)
            return toastEl;
        toastEl = document.createElement('div');
        toastEl.className = 'tc-toast';
        toastEl.setAttribute('role', 'status');
        toastEl.setAttribute('aria-live', 'polite');
        toastEl.hidden = true;
        document.body.appendChild(toastEl);
        return toastEl;
    };
    const showToast = (msg) => {
        const el = ensureToast();
        el.textContent = msg;
        el.hidden = false;
        el.classList.add('is-visible');
        clearTimeout(showToast._t);
        showToast._t = setTimeout(() => {
            el.classList.remove('is-visible');
            setTimeout(() => { el.hidden = true; }, 250);
        }, 1800);
    };

    const i18nStr = (key, fallback = '') => {
        try {
            const dict = window.tcI18n || {};
            const v = (dict[key] !== undefined && dict[key] !== null) ? dict[key] : fallback;
            return (v === undefined || v === null) ? '' : v.toString();
        }
        catch (e) {
            return (fallback || '').toString();
        }
    };
    const i18nTpl = (key, fallback, params) => {
        let s = i18nStr(key, fallback);
        if (!params)
            return s;
        Object.keys(params).forEach((k) => {
            const v = (params[k] === undefined || params[k] === null) ? '' : String(params[k]);
            s = s.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'g'), v);
        });
        return s;
    };

    const getPORMessage = () => {
        const i18n = window.tcI18n || {};
        return (i18n.priceOnRequestText || 'Prices are available on request. Please contact us.').toString();
    };
    const getPORTitle = () => {
        const i18n = window.tcI18n || {};
        return (i18n.priceOnRequestTitle || 'Prices on request').toString();
    };

    const getRootUrl = () => {
        try {
            const r = (document.body && document.body.getAttribute('data-root-url')) || '';
            if (!r || r === '/')
                return '';
            return r.replace(/\/+$/, '');
        }
        catch (e) {
            return '';
        }
    };
    const getContactUrl = () => `${getRootUrl()}/pages/contact`;
    const shouldBlockCheckout = () => !!window.tcHidePrices;
    const blockCheckout = (e) => {
        e && e.preventDefault();
        showToast(getPORMessage());
        setTimeout(() => { window.location.href = getContactUrl(); }, 350);
    };
    document.addEventListener('click', (e) => {
        if (!shouldBlockCheckout())
            return;
        const a = e.target && e.target.closest ? e.target.closest('a') : null;
        if (!a)
            return;
        const href = (a.getAttribute('href') || '').toString();
        if (!href)
            return;
        if (href.includes('/checkout'))
            return blockCheckout(e);
        if (href === '/cart/checkout' || href.includes('/cart/checkout'))
            return blockCheckout(e);
    }, true);
    document.addEventListener('submit', (e) => {
        if (!shouldBlockCheckout())
            return;
        const form = e.target;
        if (!form || !form.getAttribute)
            return;
        const action = (form.getAttribute('action') || '').toString();
        if (action.includes('/checkout'))
            return blockCheckout(e);
        const checkoutBtn = form.querySelector && form.querySelector('button[name="checkout"], input[name="checkout"]');
        if (checkoutBtn)
            return blockCheckout(e);
    }, true);

    const isZeroPriceContext = (node) => {
        try {
            if (!node || !node.closest)
                return false;
            return !!node.closest('[data-tc-zero-price="true"], [data-tc-por-product="true"]');
        }
        catch (e) {
            return false;
        }
    };
    const shouldBlockAddToCart = (node) => !!window.tcHidePrices || isZeroPriceContext(node);
    const blockAddToCart = (e) => {
        e && e.preventDefault();
        showToast(getPORMessage());
        setTimeout(() => { window.location.href = getContactUrl(); }, 350);
    };
    document.addEventListener('click', (e) => {
        if (!shouldBlockAddToCart(e.target))
            return;
        const t = e.target;
        if (!t || !t.closest)
            return;
        const hit = t.closest('[data-tc-quick-add-btn], [data-tc-qv-add], button[name="add"], input[name="add"], [data-add-to-cart]');
        if (hit)
            return blockAddToCart(e);
    }, true);
    document.addEventListener('submit', (e) => {
        if (!shouldBlockAddToCart(e.target))
            return;
        const form = e.target;
        if (!form || !form.getAttribute)
            return;
        const action = (form.getAttribute('action') || '').toString();
        if (action.includes('/cart/add'))
            return blockAddToCart(e);
        const hasId = !!(form.querySelector && form.querySelector('input[name="id"]'));
        const hasAdd = !!(form.querySelector && form.querySelector('button[name="add"], input[name="add"]'));
        if (hasId && hasAdd)
            return blockAddToCart(e);
    }, true);
    const updateCartCount = async () => {
        try {
            const cart = await fetchJson('/cart.js');
            const next = String(cart.item_count ?? 0);
            qsa('[data-cart-count]').forEach((n) => {
                n.textContent = next;
                if (n.dataset)
                    n.dataset.count = next;
            });
            qsa('[data-tc-cart-badge]').forEach((el) => {
                if (el.dataset)
                    el.dataset.count = next;
                el.setAttribute('data-count', next);
            });
        }
        catch (e) { }
    };
    const getCartDrawer = () => qs('[data-tc-cart-drawer]');
    const lockScroll = (on) => {
        document.documentElement.classList.toggle('tc-modal-open', !!on);
    };

    // Cart highlight (after add-to-cart)
    let tcCartHighlight = null;

    window.tcSetCartHighlight = (key, ttlMs = 2800) => {
        if (!key) return;
        tcCartHighlight = { key: String(key), until: Date.now() + (ttlMs || 2800) };
    };

    const applyCartHighlight = (drawer) => {
        if (!drawer || !tcCartHighlight) return;
        if (Date.now() > tcCartHighlight.until) {
            tcCartHighlight = null;
            return;
        }
        const items = Array.from(drawer.querySelectorAll('.tc-cartItem'));
        const el = items.find((n) => n.dataset && n.dataset.key === tcCartHighlight.key);
        if (!el) return;

        el.classList.add('is-highlight');
        try {
            el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } catch (e) {}

        window.clearTimeout(el.__tcHighlightT);
        el.__tcHighlightT = window.setTimeout(() => {
            el.classList.remove('is-highlight');
        }, 3000);
    };
    const renderCartDrawer = (cart) => {
        const drawer = getCartDrawer();
        if (!drawer)
            return;
        const hidePrices = !!window.tcHidePrices;
        const body = qs('[data-tc-cart-body]', drawer);
        const countEl = qs('[data-tc-cart-count]', drawer);
        const subtotalValueEl = qs('[data-tc-cart-subtotal-value]', drawer);
        const foot = qs('[data-tc-cart-foot]', drawer);
        if (countEl)
            countEl.textContent = String(cart.item_count ?? 0);
        if (subtotalValueEl)
            subtotalValueEl.textContent = formatMoneyFromCents(cart.total_price ?? 0);
        if (!body)
            return;
        if (!cart.items || !cart.items.length) {
            if (foot)
                foot.hidden = true;
            body.innerHTML = `
        <div class="tc-cartEmpty">
          <div class="tc-cartEmpty__title">Your cart is empty</div>
          <div class="tc-cartEmpty__sub">Add a part to see it here.</div>
        </div>
      `;
            return;
        }
        if (foot)
            foot.hidden = false;
        body.innerHTML = cart.items.map((it, idx) => {
            const line = idx + 1;
            const img = it.image ? `<img class="tc-cartItem__img" src="${it.image}" alt="" loading="lazy">` : `<div class="tc-cartItem__ph" aria-hidden="true"></div>`;
            const por = `<span class="tc-pricePill">${getPORTitle()}</span>`;
            const price = hidePrices ? por : (typeof it.final_line_price === 'number' ? formatMoneyFromCents(it.final_line_price) : '');
            const unit = hidePrices ? por : (typeof it.final_price === 'number' ? formatMoneyFromCents(it.final_price) : '');
            const title = (it.product_title || it.title || '').toString().replace(/"/g, '&quot;');
            const variant = (it.variant_title || '').toString();
            return `
        <div class="tc-cartItem" data-line="${line}" data-key="${it.key}" data-variant-id="${it.variant_id}">
          <a class="tc-cartItem__media" href="${it.url}">${img}</a>
          <div class="tc-cartItem__main">
            <a class="tc-cartItem__title" href="${it.url}">${title}</a>
            ${variant && variant !== 'Default Title' ? `<div class="tc-cartItem__variant">${variant}</div>` : ''}
            <div class="tc-cartItem__row">
              <div class="tc-cartItem__unit">${unit}</div>
              <div class="tc-cartQty" aria-label="Quantity">
                <button class="tc-cartQty__btn" type="button" data-tc-cart-qty="-1" aria-label="Decrease">−</button>
                <input class="tc-cartQty__input" type="number" min="0" inputmode="numeric" value="${it.quantity}" data-tc-cart-qty-input aria-label="Quantity" />
                <button class="tc-cartQty__btn" type="button" data-tc-cart-qty="1" aria-label="Increase">+</button>
              </div>
            </div>
            <div class="tc-cartItem__bottom">
              <button class="tc-cartItem__remove" type="button" data-tc-cart-remove>Remove</button>
              <div class="tc-cartItem__price">${price}</div>
            </div>
          </div>
        </div>
      `;
        }).join('');

        applyCartHighlight(body);
    };
    const fetchCart = () => fetchJson('/cart.js');
    const changeCartLine = async (line, quantity) => {
        const r = await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ line, quantity })
        });
        if (!r.ok)
            throw new Error('Cart update failed');
        return r.json();
    };
    const openCartDrawer = async () => {
        const drawer = getCartDrawer();
        if (!drawer)
            return;
        const isVisible = !drawer.hidden && drawer.classList.contains('is-open');
        if (isVisible)
            return;
        drawer.hidden = false;
        drawer.classList.remove('is-closing');
        lockScroll(true);
        const body = qs('[data-tc-cart-body]', drawer);
        if (body)
            body.innerHTML = `<div class="tc-cartLoading">${i18nStr('cartLoading', 'Loading…')}</div>`;
        requestAnimationFrame(() => {
            drawer.classList.add('is-open');
            const panel = qs('.tc-cartDrawer__panel', drawer);
            panel && panel.focus({ preventScroll: true });
        });
        try {
            const cart = await fetchCart();
            renderCartDrawer(cart);
            await updateCartCount();
        }
        catch (e) {
            if (body)
                body.innerHTML = `<div class="tc-cartLoading">${i18nStr('cartCouldNotLoad', 'Could not load cart.')}</div>`;
        }
    };
    const closeCartDrawer = () => {
        const drawer = getCartDrawer();
        if (!drawer || drawer.hidden)
            return;
        drawer.classList.remove('is-open');
        drawer.classList.add('is-closing');
        lockScroll(false);
        const panel = qs('.tc-cartDrawer__panel', drawer);
        const done = () => {
            if (!drawer.classList.contains('is-open')) {
                drawer.hidden = true;
                drawer.classList.remove('is-closing');
            }
        };
        if (panel) {
            const onEnd = (e) => {
                if (e.target !== panel)
                    return;
                done();
            };
            panel.addEventListener('transitionend', onEnd, { once: true });
            window.setTimeout(done, 700);
        }
        else {
            window.setTimeout(done, 350);
        }
    };
    
    const cartQtyState = new Map();
    const flushCartQty = async (line) => {
        const s = cartQtyState.get(line);
        if (!s)
            return;
        if (s.inFlight) {
            s.rerun = true;
            return;
        }
        s.inFlight = true;
        const qty = s.desired;
        try {
            const cart = await changeCartLine(line, qty);
            renderCartDrawer(cart);
            await updateCartCount();
        }
        catch (err) {
            showToast(i18nStr('cartCouldNotUpdate', 'Could not update cart'));
        }
        finally {
            s.inFlight = false;
            if (s.rerun) {
                s.rerun = false;
                flushCartQty(line);
            }
        }
    };
    const scheduleCartQtyUpdate = (line, qty) => {
        const prev = cartQtyState.get(line);
        const s = prev || { timer: 0, desired: qty, inFlight: false, rerun: false };
        s.desired = qty;
        if (s.timer)
            window.clearTimeout(s.timer);
        // Small debounce to batch rapid +/- taps, but update the UI immediately.
        s.timer = window.setTimeout(() => flushCartQty(line), 40);
        cartQtyState.set(line, s);
    };

const initCartDrawer = (root) => {
        const drawer = getCartDrawer();
        if (!drawer)
            return;
        if (!markBound(drawer, 'cart-drawer'))
            return;
        qsa('[data-tc-cart-toggle]', root).forEach((a) => {
            if (!markBound(a, 'cart-toggle'))
                return;
            a.addEventListener('click', (e) => {
                e.preventDefault();
                openCartDrawer();
            });
        });
        qsa('[data-tc-cart-close]', drawer).forEach((el) => {
            el.addEventListener('click', (e) => {
                e.preventDefault();
                closeCartDrawer();
            });
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && !drawer.hidden && drawer.classList.contains('is-open'))
                closeCartDrawer();
        });
        drawer.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-tc-cart-qty], [data-tc-cart-remove]');
            if (!btn)
                return;
            const item = btn.closest('.tc-cartItem');
            if (!item)
                return;
            const line = parseInt(item.getAttribute('data-line') || '0', 10);
            if (!line)
                return;
            e.preventDefault();
            if (btn.hasAttribute('data-tc-cart-remove')) {
                // Immediate remove
                cartQtyState.delete(line);
                (async () => {
                    try {
                        const cart = await changeCartLine(line, 0);
                        renderCartDrawer(cart);
                        await updateCartCount();
                    }
                    catch (err) {
                        showToast(i18nStr('cartCouldNotUpdate', 'Could not update cart'));
                    }
                })();
                return;
            }
            const delta = parseInt(btn.getAttribute('data-tc-cart-qty') || '0', 10);
            const input = qs('[data-tc-cart-qty-input]', item);
            const current = parseInt((input && input.value) || '1', 10) || 1;
            const next = Math.max(0, current + delta);
            // Optimistic UI update for snappy +/- taps.
            if (input)
                input.value = String(next);
            scheduleCartQtyUpdate(line, next);
        });
        drawer.addEventListener('change', (e) => {
            const input = e.target.closest('[data-tc-cart-qty-input]');
            if (!input)
                return;
            const item = input.closest('.tc-cartItem');
            if (!item)
                return;
            const line = parseInt(item.getAttribute('data-line') || '0', 10);
            if (!line)
                return;
            const next = Math.max(0, parseInt(input.value || '0', 10) || 0);
            // Keep field normalized.
            input.value = String(next);
            scheduleCartQtyUpdate(line, next);
        });
    };
    const initQuickAdd = (root) => {
        // Delegated binding so product cards injected later (client-side pagination/filters)
        // can still add to cart.
        if (window.__tcQuickAddDelegated)
            return;
        window.__tcQuickAddDelegated = true;

        document.addEventListener('click', async (e) => {
            const t = e.target;
            if (!t || !t.closest)
                return;
            const btn = t.closest('[data-tc-quick-add-btn]');
            if (!btn)
                return;
            // If another guard already blocked the click, do nothing.
            if (e.defaultPrevented)
                return;
            if (typeof shouldBlockAddToCart === 'function' && shouldBlockAddToCart(btn))
                return;

            const host = btn.closest('[data-tc-quick-add]');
            const id = host ? (host.getAttribute('data-variant-id') || '') : '';
            const title = host ? (host.getAttribute('data-title') || '') : '';
            if (!id)
                return;

            e.preventDefault();
            e.stopImmediatePropagation();
            e.stopPropagation();

            const textEl = btn.querySelector('.product-card__quickText');
            const prevText = (textEl ? textEl.textContent : btn.textContent) || '';
            btn.disabled = true;
            if (textEl)
                textEl.textContent = i18nStr('adding', 'Adding…');
            else
                btn.textContent = i18nStr('adding', 'Adding…');

            try {
                const fd = new FormData();
                fd.append('id', id);
                fd.append('quantity', '1');
                const r = await fetch('/cart/add.js', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'Accept': 'application/json' },
                    body: fd
                });
                if (!r.ok) {
                    let msg = i18nStr('couldNotAddToCart', 'Could not add to cart.');
                    try {
                        const err = await r.json();
                        if (err && (err.description || err.message))
                            msg = err.description || err.message;
                    }
                    catch (err) { }
                    throw new Error(msg);
                }
                // Capture the added item's key so we can highlight it inside the drawer.
                try {
                    const added = await r.json();
                    if (added && added.key && window.tcSetCartHighlight)
                        window.tcSetCartHighlight(added.key);
                }
                catch (err) { }

                // Render/open cart drawer for immediate conversion feedback.
                try {
                    const cart = await fetchCart();
                    renderCartDrawer(cart);

                    // Update cart badges without an extra fetch.
                    const next = String(cart.item_count ?? 0);
                    qsa('[data-cart-count]').forEach((n) => {
                        n.textContent = next;
                        if (n.dataset)
                            n.dataset.count = next;
                    });
                    qsa('[data-tc-cart-badge]').forEach((el) => {
                        if (el.dataset)
                            el.dataset.count = next;
                        el.setAttribute('data-count', next);
                    });

                    openCartDrawer();
                }
                catch (err) {
                    // Drawer is optional; fallback to count update.
                    await updateCartCount();
                }

                const prefix = i18nStr('addedPrefix', 'Added:');
                showToast(title ? `${prefix} ${title}` : i18nStr('addedToCart', 'Added to cart'));
            }
            catch (err) {
                showToast(err && err.message ? err.message : i18nStr('error', 'Error'));
            }
            finally {
                btn.disabled = false;
                const restore = (prevText || i18nStr('quickAdd', 'Quick add')).trim();
                if (textEl)
                    textEl.textContent = restore;
                else
                    btn.textContent = restore;
            }
        }, true);

        // Prevent any quick-add forms from doing full-page navigation if present.
        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (!form || !form.matches)
                return;
            if (form.matches('form[data-tc-quick-add]'))
                e.preventDefault();
        }, true);
    };

    const initProductForms = (root) => {
        qsa('form[data-product-form]', root).forEach((form) => {
            if (!markBound(form, 'product-form'))
                return;
            form.addEventListener('submit', async (e) => {
                const submitter = e.submitter || null;
                // If another control explicitly wants the native submit, let it pass.
                if (submitter && submitter.hasAttribute('data-no-ajax'))
                    return;
                e.preventDefault();
                e.stopImmediatePropagation();
                e.stopPropagation();
                const btn = submitter || qs('[data-add-to-cart]', form) || null;
                const prevText = btn ? btn.textContent : '';
                if (btn) {
                    btn.disabled = true;
                    btn.textContent = i18nStr('adding', 'Adding…');
                }
                try {
                    const fd = new FormData(form);
                    const r = await fetch('/cart/add.js', {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: { 'Accept': 'application/json' },
                        body: fd
                    });
                    if (!r.ok) {
                        let msg = i18nStr('couldNotAddToCart', 'Could not add to cart.');
                        try {
                            const err = await r.json();
                            if (err && (err.description || err.message))
                                msg = err.description || err.message;
                        }
                        catch (err) { }
                        throw new Error(msg);
                    }
                    // Re-render drawer so the user gets immediate feedback without leaving the PDP.
                    const cart = await fetchCart();
                    renderCartDrawer(cart);
                    await updateCartCount();
                    openCartDrawer();
                    showToast(i18nStr('addedToCart', 'Added to cart'));
                }
                catch (err) {
                    showToast(err && err.message ? err.message : i18nStr('error', 'Error'));
                }
                finally {
                    if (btn) {
                        btn.disabled = false;
                        btn.textContent = (prevText || i18nStr('addToCart', 'Add to cart')).trim();
                    }
                }
            }, { capture: true });
        });
    };

    let revealObserver = null;
    const getRevealObserver = () => {
        if (revealObserver)
            return revealObserver;
        revealObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting)
                    return;
                entry.target.classList.add('is-visible');
                revealObserver.unobserve(entry.target);
            });
        }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });
        return revealObserver;
    };
    const initReveal = (root) => {
        const obs = getRevealObserver();
        const scope = (root && root.querySelector) ? (root.querySelector('#MainContent') || root) : root;
        const els = qsa('.tc-reveal, .tc-homeHero, .tc-slab, [data-tc-animate], .product-card, .card, .tc-cardSquare, .tc-faq, .tc-review, .tc-aboutNew__hiCard, .tc-aboutNew__policy, .tc-aboutNew__tabNav, .tc-aboutNew__tabPanels, .tc-aboutNew__panel, .tc-contactTabs, .tc-contactTabs__item, .tc-contactGrid, .tc-team, .tc-team__title, .tc-team__sub, .tc-teamGrid, .tc-teamCard, .tc-reviewsGrid', scope);
        const toObserve = [];
        els.forEach((el, idx) => {
            if (!el)
                return;
            if (!el.classList.contains('tc-reveal'))
                el.classList.add('tc-reveal');
            if (el.classList.contains('is-visible'))
                return;
            if (el.dataset.tcRevealBound === '1')
                return;
            el.dataset.tcRevealBound = '1';
            if (!el.style.getPropertyValue('--tc-reveal-delay')) {
                const d = (idx % 7) * 55;
                el.style.setProperty('--tc-reveal-delay', `${d}ms`);
            }
            toObserve.push(el);
        });
        if (toObserve.length) {
            // Defer observer attachment by one frame so the initial "hidden" state can paint first.
            // This avoids a no-animation case where elements start in-view and become visible in the same tick.
            requestAnimationFrame(() => {
                toObserve.forEach((el) => {
                    try {
                        if (!el || el.classList.contains('is-visible'))
                            return;
                        obs.observe(el);
                    }
                    catch (e) { }
                });
            });
        }
    };

    const initAccordionGroups = (root) => {
        try {
            const scope = root || document;
            const groups = qsa('[data-tc-accordion-group]', scope);
            groups.forEach((group) => {
                const items = qsa('details', group);
                if (!items.length)
                    return;
                const mode = (group.getAttribute('data-tc-accordion-group') || '').toLowerCase();
                const isMulti = mode === 'multi' || mode === 'multiple';

                items.forEach((d) => {
                    if (!d || d.dataset.tcAccordionBound === '1')
                        return;
                    d.dataset.tcAccordionBound = '1';
                    d.addEventListener('toggle', () => {
                        if (!d.open)
                            return;
                        if (isMulti)
                            return;
                        items.forEach((o) => {
                            if (o !== d)
                                o.removeAttribute('open');
                        });
                    });
                });
            });
        }
        catch (e) { }
    };

    const initHeroParallax = () => {
        if (!window.matchMedia || window.matchMedia('(prefers-reduced-motion: reduce)').matches)
            return;
        const heroes = qsa('.tc-homeHero');
        if (!heroes.length)
            return;
        const onScroll = rafThrottle(() => {
            const vh = Math.max(1, window.innerHeight || 1);
            heroes.forEach((hero) => {
                const r = hero.getBoundingClientRect();
                const t = (r.top + r.height * 0.2 - vh * 0.5) / vh;
                const offset = Math.max(-22, Math.min(22, t * -18));
                hero.style.setProperty('--tc-hero-parallax', `${offset.toFixed(1)}px`);
            });
        });
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        onScroll();
    };
    const extractCodes = (text) => {
        const s = (text || '').toString().toUpperCase();
        const out = new Set();
        const reDotted = /\b([A-Z]{2,5})\s*\.?\s*(\d{2,4})(?:\s*\.?\s*(\d{2,4})){1,4}\b/g;
        let m;
        while ((m = reDotted.exec(s))) {
            const full = m[0].replace(/\s+/g, '').replace(/\.+/g, '.').replace(/\.$/, '');
            out.add(full);
        }
        const reAlphaNum = /\b([A-Z]{1,4}\d{3,8})\b/g;
        while ((m = reAlphaNum.exec(s)))
            out.add(m[1]);
        const reLoose = /\b(?=[A-Z0-9]*[A-Z])(?=[A-Z0-9]*\d)[A-Z0-9.\-]{5,}\b/g;
        while ((m = reLoose.exec(s)))
            out.add(m[0].replace(/\.+/g, '.'));
        return Array.from(out);
    };
    const copyText = async (txt) => {
        const t = (txt || '').toString();
        if (!t)
            return false;
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(t);
                return true;
            }
        }
        catch (e) { }
        try {
            const ta = document.createElement('textarea');
            ta.value = t;
            ta.setAttribute('readonly', 'true');
            ta.style.position = 'fixed';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            ta.remove();
            return true;
        }
        catch (e) { }
        return false;
    };
    const tagSvg = (name) => {
        switch ((name || '').toString()) {
            case 'truck':
                return '<svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none"><path d="M3 7h11v10H3V7Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M14 10h4l3 3v4h-7V10Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M7 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm12 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" fill="currentColor"/></svg>';
            case 'badge':
                return '<svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none"><path d="M12 2l3 3 4 1-1 4 1 4-4 1-3 3-3-3-4-1 1-4-1-4 4-1 3-3Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M9 12l2 2 4-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
            case 'sparkle':
            default:
                return '<svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none"><path d="M12 2l1.2 4.2L17 8l-3.8 1.8L12 14l-1.2-4.2L7 8l3.8-1.8L12 2Z" fill="currentColor"/><path d="M19 12l.8 2.8L22 16l-2.2 1.2L19 20l-.8-2.8L16 16l2.2-1.2L19 12Z" fill="currentColor"/></svg>';
        }
    };
    const initProductCodes = (root) => {
        qsa('[data-tc-card]', root).forEach((card) => {
            if (!markBound(card, 'codes'))
                return;
            const titleEl = qs('.product-card__title', card);
            const codeEl = qs('[data-tc-code]', card);
            const copyBtn = qs('[data-tc-copy-code]', card);
            const title = titleEl ? titleEl.textContent : '';

            // Prefer server-provided code if present (SKU/title parsed in Liquid).
            let code = '';
            if (codeEl) {
                const existing = ((codeEl.dataset && codeEl.dataset.tcCodeValue) ? codeEl.dataset.tcCodeValue : (codeEl.textContent || '')).trim();
                if (existing && existing !== '—')
                    code = existing.toUpperCase();
            }

            if (!code) {
                const codes = extractCodes(title);
                const preferred = codes.find((c) => /^(TCK|TCM|TCW)\./.test((c || '').toString()));
                code = (preferred || codes[0] || '').toString().trim();
            }

            if (codeEl) {
                codeEl.textContent = code || '—';
                codeEl.dataset.tcCodeValue = code;
            }
            if (copyBtn) {
                copyBtn.disabled = !code;
                copyBtn.addEventListener('click', async () => {
                    const current = (codeEl && (codeEl.dataset.tcCodeValue || codeEl.textContent)) ? (codeEl.dataset.tcCodeValue || codeEl.textContent || '') : code;
                    const ok = await copyText((current || '').toString().trim());
                    showToast(ok ? i18nStr('codeCopied', 'Code copied') : i18nStr('copyFailed', 'Copy failed'));
                });
            }
        });
    };
    const ensureQuickView = () => {
        let host = qs('.tc-qv');
        if (host)
            return host;
        host = document.createElement('div');
        host.className = 'tc-qv';
        host.innerHTML = `
      <div class="tc-qv__overlay" data-tc-qv-close></div>
      <div class="tc-qv__panel" role="dialog" aria-modal="true" aria-label="${i18nStr('quickView', 'Quick view')}">
        <div style="padding: 14px 14px 0 14px; display:flex; justify-content:flex-end">
          <button class="tc-qv__close" type="button" data-tc-qv-close aria-label="${i18nStr('close', 'Close')}">✕</button>
        </div>
        <div class="tc-qv__content"></div>
      </div>
    `;
        document.body.appendChild(host);
        qsa('[data-tc-qv-close]', host).forEach((el) => el.addEventListener('click', () => closeQuickView()));
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && host.classList.contains('is-open'))
                closeQuickView();
        });
        return host;
    };
    const openQuickView = async (handle) => {
        const qv = ensureQuickView();
        const content = qs('.tc-qv__content', qv);
        if (!content)
            return;
        qv.classList.add('is-open');
        document.documentElement.classList.add('tc-modal-open');
        content.innerHTML = `<div class="tc-qv__grid"><div class="tc-qv__media"></div><div class="stack" style="gap:10px"><div class="card card__pad">${i18nStr('loading', 'Loading…')}</div></div></div>`;
        let p;
        try {
            p = await fetchJson(`/products/${handle}.js`);
        }
        catch (e) {
            content.innerHTML = `<div class="card card__pad"><strong>${i18nStr('couldNotLoadProduct', 'Could not load product.')}</strong></div>`;
            return;
        }
        const title = p.title || '';
        const vendor = p.vendor || '';
        const body = stripHtml(p.description || p.body_html || '');
        const codes = extractCodes(`${title} ${body}`);
        const code = codes[0] || '';
        const img = (p.images && p.images[0]) ? p.images[0] : '';
        const variants = Array.isArray(p.variants) ? p.variants : [];
        const hidePrices = !!window.tcHidePrices;
        const firstAvail = variants.find((v) => v.available) || variants[0] || {};
        const money = (v) => formatMoneySafe((v && v.price != null) ? (Number(v.price) / 100) : '');
        const compare = (firstAvail.compare_at_price && firstAvail.compare_at_price > firstAvail.price) ? firstAvail.compare_at_price : null;
        const variantLabel = i18nStr('variant', 'Variant');
        const defaultVariant = i18nStr('defaultVariant', 'Default');
        const soldOut = i18nStr('soldOut', 'Sold out');
        const brandLabel = i18nStr('brand', 'Brand');
        const codeLabel = i18nStr('code', 'Code');
        const requestQuote = i18nStr('requestQuote', 'Request a quote');
        const addToCart = i18nStr('addToCart', 'Add to cart');
        const viewDetails = i18nStr('viewDetails', 'View details');
        const copyCode = i18nStr('copyCode', 'Copy code');
        const variantSelect = variants.length > 1 ? `
      <label style="font-weight:800; font-size:12px; letter-spacing:.08em; text-transform:uppercase; color: rgba(11,18,32,.58)">${variantLabel}</label>
      <select class="input" data-tc-qv-variant>
        ${variants.map((v) => `<option value="${v.id}" ${!v.available ? 'disabled' : ''} ${v.id === firstAvail.id ? 'selected' : ''}>${(v.title || defaultVariant)}${!v.available ? ` — ${soldOut}` : ''}</option>`).join('')}
      </select>
    ` : `<input type="hidden" data-tc-qv-variant value="${firstAvail.id || ''}">`;
        content.innerHTML = `
      <div class="tc-qv__grid">
        <div class="tc-qv__media">${img ? `<img src="${img}" alt="">` : ''}</div>
        <div class="stack" style="gap:12px; padding: 2px 2px 16px 2px">
          <h3 class="tc-qv__title">${title}</h3>
          <div class="tc-qv__meta">${vendor ? `${brandLabel}: <strong>${vendor}</strong>` : ''}${code ? ` · ${codeLabel}: <strong>${code}</strong>` : ''}</div>
          ${hidePrices ? '' : `
          <div class="tc-qv__priceRow">
            <div class="price" style="font-size:18px; font-weight:950">${money(firstAvail)}</div>
            ${compare ? `<div class="price--compare" style="font-size:14px">${formatMoneySafe(Number(compare) / 100)}</div>` : ''}
          </div>`}

          <div class="stack" style="gap:8px">
            ${variantSelect}
            <div class="tc-qv__actions">
              ${hidePrices ? `<a class="btn" href="/pages/contact?product=${encodeURIComponent(handle)}">${requestQuote}</a>` : `<button class="btn" type="button" data-tc-qv-add ${!firstAvail.available ? 'disabled' : ''}>${addToCart}</button>`}
              <a class="btn btn--secondary" href="/products/${handle}">${viewDetails}</a>
              ${code ? `<button class="btn btn--secondary" type="button" data-tc-qv-copy>${copyCode}</button>` : ''}
            </div>
          </div>

          ${body ? `<div class="card"><div class="card__pad rte" style="font-size:14px; color: rgba(11,18,32,.72)">${body.slice(0, 420)}${body.length > 420 ? '…' : ''}</div></div>` : ''}
        </div>
      </div>
    `;
        const variantEl = qs('[data-tc-qv-variant]', content);
        const addBtn = qs('[data-tc-qv-add]', content);
        const copyBtn = qs('[data-tc-qv-copy]', content);
        if (copyBtn)
            copyBtn.addEventListener('click', async () => {
                const ok = await copyText(code);
                showToast(ok ? i18nStr('codeCopied', 'Code copied') : i18nStr('copyFailed', 'Copy failed'));
            });
        if (addBtn)
            addBtn.addEventListener('click', async () => {
                const id = variantEl ? variantEl.value : (firstAvail.id || '');
                if (!id)
                    return;
                addBtn.disabled = true;
                const prev = addBtn.textContent;
                addBtn.textContent = i18nStr('adding', 'Adding…');
                try {
                    const fd = new FormData();
                    fd.append('id', id);
                    fd.append('quantity', '1');
                    const r = await fetch('/cart/add.js', {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: { 'Accept': 'application/json' },
                        body: fd
                    });
                    if (!r.ok)
                        throw new Error(i18nStr('couldNotAddToCart', 'Could not add to cart'));
                    await updateCartCount();
                    showToast(i18nStr('addedToCart', 'Added to cart'));
                    closeQuickView();
                }
                catch (e) {
                    showToast(i18nStr('errorAddingToCart', 'Error adding to cart'));
                }
                finally {
                    addBtn.disabled = false;
                    addBtn.textContent = prev;
                }
            });
    };
    const closeQuickView = () => {
        const qv = qs('.tc-qv');
        if (!qv)
            return;
        qv.classList.remove('is-open');
        document.documentElement.classList.remove('tc-modal-open');
    };
    const initQuickViewTriggers = (root) => {
        qsa('[data-tc-quickview]', root).forEach((btn) => {
            if (!markBound(btn, 'qv-trigger'))
                return;
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const handle = btn.getAttribute('data-handle');
                if (!handle)
                    return;
                openQuickView(handle);
            });
        });
    };
    const initProductSpecs = () => {
        const section = qs('[data-section-type="main-product"]');
        if (!section)
            return;
        const hostV2 = qs('[data-tc-specs-v2]', section);

        // V2 layout (Model / OEM numbers / Side / Condition + V/M/W/D table)
        if (hostV2) {
            if (!markBound(hostV2, 'specs-v2'))
                return;

            const jsonEl = qs('script[data-product-json]', section);
            if (!jsonEl)
                return;

            let p;
            try {
                p = JSON.parse(jsonEl.textContent || '{}');
            }
            catch (e) {
                p = {};
            }

            const asString = (v) => (v == null ? '' : String(v));
            const cleanValue = (v) => asString(v).replace(/\s+/g, ' ').trim();

            const descEl = qs('script[data-tc-description]', section);
            let descHtml = '';
            try {
                descHtml = descEl ? JSON.parse(descEl.textContent || '""') : '';
            }
            catch (e) {
                descHtml = '';
            }
            if (!descHtml)
                descHtml = p.description || p.body_html || '';
            descHtml = asString(descHtml);

            const htmlToText = (html) => {
                const pre = asString(html)
                    .replace(/<\s*br\s*\/?>/gi, '\n')
                    .replace(/<\/\s*(p|div|li|tr|h[1-6])\s*>/gi, '\n')
                    .replace(/<\/\s*td\s*>/gi, '\t')
                    .replace(/<\/\s*th\s*>/gi, '\t');
                const tmp = document.createElement('div');
                tmp.innerHTML = pre;
                const txt = asString(tmp.textContent || '')
                    .replace(/\r/g, '')
                    .replace(/\u00a0/g, ' ');
                return txt
                    .replace(/[ \t]+\n/g, '\n')
                    .replace(/\n{3,}/g, '\n\n')
                    .trim();
            };

            const text = htmlToText(descHtml);
            const combined = `${asString(p.title || '')}\n${text}`;

            const i18n = {
                dash: hostV2.dataset.tcI18nDash || '—',
                sideLeft: hostV2.dataset.tcI18nSideLeft || 'Left',
                sideRight: hostV2.dataset.tcI18nSideRight || 'Right',
                sideSteer: hostV2.dataset.tcI18nSideSteer || 'Steer',
                sideFront: hostV2.dataset.tcI18nSideFront || 'Front',
                sideRear: hostV2.dataset.tcI18nSideRear || 'Rear',
                conditionNew: hostV2.dataset.tcI18nConditionNew || 'NEW',
                conditionRemanufactured: hostV2.dataset.tcI18nConditionRemanufactured || 'Remanufactured',
            };

            const extractModel = () => {
                const m1 = text.match(/product\s*type\s*[:\-]\s*([^\n]+)/i);
                if (m1)
                    return cleanValue(m1[1]);
                const m2 = text.match(/brand\s*and\s*type\s*[:\-]\s*([^\n]+)/i);
                if (m2)
                    return cleanValue(m2[1]);
                const m3 = text.match(/\bmodel\s*[:\-]\s*([^\n]+)/i);
                if (m3)
                    return cleanValue(m3[1]);
                const pt = p.product_type || p.type;
                if (pt)
                    return cleanValue(pt);
                return cleanValue(hostV2.getAttribute('data-tc-model-default') || '');
            };
	            const extractOems = () => {
	                const srcTextRaw = combined || '';
	                if (!srcTextRaw) return [];
	                // Some product descriptions mistakenly use Cyrillic homoglyphs for "OEM" (ОЕМ).
	                // Normalize those to Latin so our parsers work reliably.
	                const srcText = srcTextRaw
	                    .replace(/[Оо]/g, 'O')
	                    .replace(/[Ее]/g, 'E')
	                    .replace(/[Мм]/g, 'M');

                const hasDigit = (s) => /\d/.test(s);
                const normalizeText = (s) => String(s || '')
                    .replace(/\u00a0/g, ' ')
                    .replace(/\r/g, '\n');

                const pushUnique = (arr, v) => {
                    if (!v)
                        return;
                    if (arr.indexOf(v) === -1)
                        arr.push(v);
                };

                const tokenizeChunk = (chunk) => {
                    const out = [];
                    if (!chunk)
                        return out;

                    const addTokensFromLine = (line) => {
                        const trimmed = (line || '').trim();
                        if (!trimmed)
                            return;

                        // Stop if we hit another labeled section
                        if (/^(?:brand(?:\s*(?:and|&)\s*type)?|vehicle(?:s)?|model|wheel|date|side|condition|type)\b/i.test(trimmed))
                            return 'stop';

                        // If line looks like "KB: SN5011RC" keep it as one item
                        if (/^[A-Z]{1,10}\s*:\s*[A-Za-z0-9][A-Za-z0-9\-\/]{2,}$/i.test(trimmed) && hasDigit(trimmed)) {
                            pushUnique(out, trimmed.replace(/\s+/g, ' '));
                            return;
                        }

                        trimmed.split(/[\s,;]+/).forEach((part) => {
                            if (out.length >= 5)
                                return;
                            const tok = (part || '').trim().replace(/^[()]+|[()]+$/g, '');
                            if (!tok)
                                return;
                            if (!hasDigit(tok))
                                return;
                            // Skip pure years and other short numeric tokens (e.g. 1996, 2000)
                            if (/^\d{1,4}$/.test(tok))
                                return;
                            if (tok.length < 3)
                                return;
                            if (/^(?:oem|brand|type|vehicle|vehicles|model|wheel|date|side|condition)$/i.test(tok))
                                return;
                            pushUnique(out, tok);
                        });
                    };

                    const lines = normalizeText(chunk).split(/\n+/);
                    for (const line of lines) {
                        if (out.length >= 5)
                            break;
                        const res = addTokensFromLine(line);
                        if (res === 'stop') {
                            if (out.length)
                                break;
                            continue;
                        }
                    }

                    if (!out.length) {
                        // Fallback for single-line blobs
                        const flat = normalizeText(chunk).replace(/\n/g, ' ');
                        flat.split(/[\s,;]+/).forEach((part) => {
                            if (out.length >= 5)
                                return;
                            const tok = (part || '').trim().replace(/^[()]+|[()]+$/g, '');
                            if (!tok)
                                return;
                            if (!hasDigit(tok))
                                return;
                            if (/^\d{1,4}$/.test(tok))
                                return;
                            if (tok.length < 3)
                                return;
                            if (/^(?:oem|brand|type|vehicle|vehicles|model|wheel|date|side|condition)$/i.test(tok))
                                return;
                            pushUnique(out, tok);
                        });
                    }

                    return out.slice(0, 5);
                };

                // A) Prefer explicit OEM label in plain text
                const labelRe = /OEM\s*(?:No\.?|Nos\.?|Number(?:s)?|#)\b\s*(?:[:\-–—]\s*)?([\s\S]{0,3000})/i;
                const m = srcText.match(labelRe);
                if (m && m[1]) {
                    const out = tokenizeChunk(m[1]);
                    if (out.length)
                        return out;
                }

                // B) DOM-based fallback: find nodes containing OEM label and read nearby content
                try {
                    const doc = new DOMParser().parseFromString(descHtml || '', 'text/html');
                    const nodes = Array.from(doc.querySelectorAll('p, li, div, td, th, span, strong, b'));
                    for (const node of nodes) {
                        const t = (node.textContent || '').trim();
                        if (!t)
                            continue;
                        if (!/OEM\s*(?:No\.?|Nos\.?|Number(?:s)?|#)/i.test(t))
                            continue;

                        const candidates = [];
                        candidates.push(t);
                        if (node.parentElement)
                            candidates.push((node.parentElement.textContent || '').trim());
                        if (node.nextElementSibling)
                            candidates.push((node.nextElementSibling.textContent || '').trim());
                        if (node.innerHTML)
                            candidates.push(node.innerHTML.replace(/<br\s*\/?\s*>/gi, '\n').replace(/<\/?[^>]+>/g, ' ').trim());

                        for (const c of candidates) {
                            const out = tokenizeChunk(c);
                            if (out.length)
                                return out;
                        }
                    }
                }
                catch (e) {
                    // ignore
                }

                // C) Last fallback: PREFIX: VALUE pairs anywhere
                const outPairs = [];
                const pairSrc = normalizeText(srcText);
                const pairRe = /\b([A-Z]{2,10})\s*:\s*([A-Za-z0-9][A-Za-z0-9\-\/]{1,30})\b/g;
                let pm;
                while ((pm = pairRe.exec(pairSrc))) {
                    if (outPairs.length >= 5)
                        break;
                    const prefix = (pm[1] || '').toUpperCase();
                    const value = pm[2] || '';
                    if (!hasDigit(value))
                        continue;
                    if (['NOTE', 'OEM', 'VEHICLE', 'VEHICLES', 'MODEL', 'WHEEL', 'DATE', 'SIDE', 'CONDITION', 'BRAND', 'TYPE'].includes(prefix))
                        continue;
                        // Keep only the OEM code itself (without the prefix like KB/MAN).
                        pushUnique(outPairs, value);
                }
                return outPairs.slice(0, 5);
            };



            const extractSide = () => {
                const s = combined.toLowerCase();
                if (s.includes('left'))
                    return i18n.sideLeft;
                if (s.includes('right'))
                    return i18n.sideRight;
                if (s.includes('steer'))
                    return i18n.sideSteer;
                if (s.includes('front'))
                    return i18n.sideFront;
                if (s.includes('rear'))
                    return i18n.sideRear;
                return i18n.dash;
            };
            const extractCondition = () => {
                const title = asString(p.title || '').toUpperCase();
                // Business rule: all products are NEW by default.
                // If the product title contains TCK / TCW / TCM (Brake calipers), treat it as Remanufactured.
                const isCaliper = /(?:^|[^A-Z0-9])(TCK|TCW|TCM)(?:[^A-Z0-9]|$)/.test(title);
                return isCaliper ? i18n.conditionRemanufactured : i18n.conditionNew;
            };

            const extractFitmentRows = () => {
                const rows = [];
                try {
                    const doc = new DOMParser().parseFromString(descHtml || '', 'text/html');
                    const tables = Array.from(doc.querySelectorAll('table'));
                    for (const table of tables) {
                        const trs = Array.from(table.querySelectorAll('tr'));
                        if (trs.length < 2)
                            continue;
                        const headCells = Array.from(trs[0].querySelectorAll('th,td')).map((c) => cleanValue(c.textContent).toLowerCase());
                        let idxV = headCells.findIndex((t) => t.includes('vehicle'));
                        let idxM = headCells.findIndex((t) => t.includes('model'));
                        let idxW = headCells.findIndex((t) => t.includes('wheel'));
                        let idxD = headCells.findIndex((t) => t.includes('date'));
                        if (idxV === -1 && headCells.length === 4)
                            idxV = 0;
                        if (idxM === -1 && headCells.length === 4)
                            idxM = 1;
                        if (idxW === -1 && headCells.length === 4)
                            idxW = 2;
                        if (idxD === -1 && headCells.length === 4)
                            idxD = 3;
                        const ok = [idxV, idxM, idxW, idxD].every((x) => x >= 0);
                        if (!ok)
                            continue;
                        for (let i = 1; i < trs.length; i++) {
                            const cells = Array.from(trs[i].querySelectorAll('td,th'));
                            if (!cells.length)
                                continue;
                            const getCell = (idx) => {
                                const val = cleanValue(cells[idx] ? cells[idx].textContent : '');
                                return val || '—';
                            };
                            const row = [getCell(idxV), getCell(idxM), getCell(idxW), getCell(idxD)];
                            if (row.every((x) => x === '—'))
                                continue;
                            rows.push(row);
                        }
                        if (rows.length)
                            break;
                    }
                }
                catch (e) {
                    /* ignore */
                }

                if (!rows.length) {
                    const kv = {};
                    const mkv = (key) => {
                        const m = text.match(new RegExp(`${key}\\s*[:\\-]\\s*([^\\n]+)`, 'i'));
                        if (m)
                            kv[key] = cleanValue(m[1]);
                    };
                    mkv('vehicle');
                    mkv('model');
                    mkv('wheel');
                    mkv('date');
                    if (kv.vehicle || kv.model || kv.wheel || kv.date) {
                        rows.push([kv.vehicle || '—', kv.model || '—', kv.wheel || '—', kv.date || '—']);
                    }
                }

                return rows;
            };

            const setText = (sel, value) => {
                const el = qs(sel, hostV2);
                if (el)
                    el.textContent = cleanValue(value) || '—';
            };

            setText('[data-tc-spec-model]', extractModel());
            setText('[data-tc-spec-side]', extractSide());
            setText('[data-tc-spec-condition]', extractCondition());

            const oemHost = qs('[data-tc-spec-oem]', hostV2);
            if (oemHost) {
                const oems = extractOems();
                oemHost.innerHTML = '';
                (oems.length ? oems : ['—']).forEach((val) => {
                    const li = document.createElement('li');
                    li.textContent = val;
                    oemHost.appendChild(li);
                });
            }

            const tbody = qs('[data-tc-spec-fitment]', hostV2);
            if (tbody) {
                const rows = extractFitmentRows().slice(0, 10);
                tbody.innerHTML = '';
                const safeRows = rows.length ? rows : [['—', '—', '—', '—']];
                for (const r of safeRows) {
                    const tr = document.createElement('tr');
                    for (const cellVal of r) {
                        const td = document.createElement('td');
                        td.textContent = cleanValue(cellVal) || '—';
                        tr.appendChild(td);
                    }
                    tbody.appendChild(tr);
                }
            }

            return;
        }

        // Legacy layout (kept for backwards compatibility)
        const host = qs('[data-tc-specs]', section);
        const grid = qs('[data-tc-specs-grid]', section);
        if (!host || !grid)
            return;
        if (!markBound(host, 'specs'))
            return;
        const jsonEl = qs('script[data-product-json]', section);
        if (!jsonEl)
            return;
        let p;
        try {
            p = JSON.parse(jsonEl.textContent || '{}');
        }
        catch (e) {
            p = {};
        }
        const title = p.title || '';
        const vendor = p.vendor || '';
        const desc = stripHtml(p.description || '');
        const codes = extractCodes(`${title} ${desc}`);
        const code = codes[0] || '';
        const specs = [];
        if (code)
            specs.push(['Part code', code]);
        if (vendor)
            specs.push(['Brand', vendor]);
        if (p.variants && p.variants[0] && p.variants[0].sku)
            specs.push(['SKU', p.variants[0].sku]);
        let condition = 'New';
        const titleUpper = `${p.title || ''}`.toUpperCase();
        if (/(?:^|[^A-Z0-9])(TCK|TCW|TCM)(?:[^A-Z0-9]|$)/.test(titleUpper))
            condition = 'Remanufactured';
        specs.push(['Condition', condition]);
        specs.push(['Support', 'Warranty & B2B options']);

        const isNoisyKey = (key) => {
            const k = (key || '').toLowerCase();
            if (!k)
                return true;
            // Remove the entire "Vehicle Model / Wheel / Date" block and similar clutter
            if (k.includes('vehicle model') || k === 'vehicle' || k === 'model' || k === 'wheel' || k === 'date')
                return true;
            if (k.includes('vehicle') || k.includes('wheel'))
                return true;
            return false;
        };
        const lines = (desc || '').split(/\n|\r|\u2022|\u00b7|\t/).map((x) => x.trim()).filter(Boolean);
        for (const ln of lines) {
            const m = ln.match(/^([A-Za-z][A-Za-z0-9\s\-\/]{2,18})\s*[:\-]\s*(.{2,})$/);
            if (!m)
                continue;
            const k = m[1].trim();
            const v = m[2].trim();
            if (!k || !v)
                continue;
            const kk = k.replace(/\s+/g, ' ');
            const vv = v.replace(/\s+/g, ' ');
            if (isNoisyKey(kk))
                continue;
            if (code && vv.toLowerCase() === code.toLowerCase())
                continue;
            if (vendor && vv.toLowerCase() === vendor.toLowerCase())
                continue;
            if (specs.some((s) => s[0].toLowerCase() === kk.toLowerCase()))
                continue;
            specs.push([kk, vv]);
            if (specs.length >= 9)
                break;
        }
        grid.innerHTML = specs.map(([k, v]) => `
      <div class="tc-spec">
        <div class="tc-spec__k">${k}</div>
        <div class="tc-spec__v">${v}</div>
      </div>
    `).join('');
        const tagsHost = qs('[data-tc-specs-tags]', section);
        if (tagsHost)
            tagsHost.innerHTML = '';
    };

    const initProductGallery = () => {
        const section = qs('[data-section-type="main-product"]');
        if (!section)
            return;
        const gallery = qs('[data-tc-product-gallery]', section);
        if (!gallery)
            return;
        const items = qsa('[data-tc-product-media]', gallery);
        const thumbs = qsa('[data-tc-product-thumb]', gallery);
        if (!items.length || !thumbs.length)
            return;
        if (!markBound(gallery, 'product-gallery'))
            return;
        const activate = (id, opts = {}) => {
            if (!id)
                return;
            items.forEach((el) => {
                const match = el.getAttribute('data-tc-product-media') === id;
                if (match)
                    el.removeAttribute('hidden');
                else
                    el.setAttribute('hidden', '');
            });
            thumbs.forEach((b) => {
                const match = b.getAttribute('data-tc-product-thumb') === id;
                b.setAttribute('aria-current', match ? 'true' : 'false');
            });
            if (opts.updateHash) {
                try {
                    const u = new URL(window.location.href);
                    u.hash = `tcMedia-${id}`;
                    window.history.replaceState({}, '', u.toString());
                }
                catch (e) { }
            }
        };
        thumbs.forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const id = btn.getAttribute('data-tc-product-thumb');
                activate(id, { updateHash: true });
            });
        });
        const initFromUrl = () => {
            const hash = (window.location.hash || '').replace('#', '');
            if (hash.startsWith('tcMedia-')) {
                const id = hash.replace('tcMedia-', '').trim();
                if (id)
                    return activate(id);
            }
            // Back-compat: links that used #media-<id>
            if (hash.startsWith('media-')) {
                const id = hash.replace('media-', '').trim();
                if (id)
                    return activate(id);
            }
            // Default to the first item
            const first = items[0]?.getAttribute('data-tc-product-media');
            if (first)
                activate(first);
        };
        window.addEventListener('hashchange', initFromUrl);
        initFromUrl();
    };

    const cleanupProductDescription = () => {
        const section = qs('[data-section-type="main-product"]');
        if (!section)
            return;
        const rte = qs('.rte', section);
        if (!rte)
            return;
        if (!markBound(rte, 'desc-clean'))
            return;
        const shouldDrop = (t) => {
            const s = (t || '').toLowerCase();
            if (!s)
                return false;
            return s.includes('vehicle model') || (s.includes('wheel') && s.includes('date'));
        };
        qsa('table, p, div, ul, ol, dl', rte).forEach((el) => {
            if (shouldDrop(el.textContent || ''))
                el.remove();
        });
    };
    const normalizeProductsCollectionLinks = (root = document) => {
        const FROM = '/collections/all';
        const TO = '/collections/products';
        root.querySelectorAll('a[href]')?.forEach?.((a) => {
            const href = a.getAttribute('href');
            if (!href)
                return;
            if (/^(mailto:|tel:|javascript:|#)/i.test(href))
                return;
            if (href.indexOf(FROM) === -1)
                return;
            a.setAttribute('href', href.replace(FROM, TO));
        });
        try {
            const p = window.location.pathname || '';
            if (p.endsWith(FROM)) {
                const nextPath = p.replace(FROM, TO);
                window.location.replace(nextPath + window.location.search + window.location.hash);
            }
        }
        catch (e) { }
    };

    const normalizeLocaleInternalLinks = (root = document) => {
        // English is the primary language without a prefix. Other languages use a /xx prefix (e.g., /et, /fi).
        try {
            const p = window.location.pathname || '';
            const m = p.match(/^\/([a-z]{2})(?:\/|$)/i);
            if (!m)
                return;
            const prefix = `/${m[1].toLowerCase()}`;
            if (prefix === '/en')
                return;
            root.querySelectorAll('a[href^="/"]')?.forEach?.((a) => {
                const href = a.getAttribute('href') || '';
                if (!href || href.startsWith('//'))
                    return;
                if (href === prefix || href.startsWith(prefix + '/'))
                    return;
                // If the href already targets another locale prefix (/fi, /et, ...), leave it as-is.
                if (/^\/[a-z]{2}(?:\/|$)/i.test(href))
                    return;
                // Skip links that should not be locale-prefixed
                if (href.startsWith('/admin') || href.startsWith('/api'))
                    return;
                a.setAttribute('href', prefix + href);
            });
        }
        catch (e) { }
    };
    const init = (root = document) => {
        normalizeProductsCollectionLinks(root);
        normalizeLocaleInternalLinks(root);
        initHeaderMotion();
        initHeroParallax();
        initCartDrawer(root);
        bindSmartSearchForms(root);
        initSmartSearchDropdown(root);
        initQuickAdd(root);
        initProductForms(root);
        initProductCodes(root);
        initReveal(root);
        initAccordionGroups(root);
        initProductSpecs();
        initProductGallery();
        cleanupProductDescription();
    };
    const onReady = (fn) => {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', fn, { once: true });
        }
        else {
            fn();
        }
    };
    onReady(() => {
        init(document);
        initSmartSearchPage();
        setTimeout(() => { updateCartCount(); }, 40);
        document.addEventListener('shopify:section:load', (e) => init(e.target));
        document.addEventListener('shopify:section:reorder', () => init(document));
    });
})();
(() => {
    const card = document.querySelector('[data-tc-auth-card]');
    if (!card)
        return;
    const setView = (view) => {
        const v = (view === 'recover') ? 'recover' : 'login';
        card.setAttribute('data-view', v);
        card.querySelectorAll('[data-pane]').forEach((pane) => {
            const isActive = pane.getAttribute('data-pane') === v;
            pane.setAttribute('aria-hidden', String(!isActive));
        });
    };
    const fromHash = () => {
        const h = (window.location.hash || '').toLowerCase();
        if (h === '#recover')
            return 'recover';
        return 'login';
    };
    setView(fromHash());
    document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-tc-auth-switch]');
        if (!btn)
            return;
        e.preventDefault();
        const view = btn.getAttribute('data-tc-auth-switch');
        if (view === 'recover') {
            history.replaceState({}, '', '#recover');
            setView('recover');
            const input = document.getElementById('RecoverEmail');
            input && setTimeout(() => input.focus({ preventScroll: true }), 50);
        }
        else {
            history.replaceState({}, '', '#');
            setView('login');
            const input = document.getElementById('CustomerEmail');
            input && setTimeout(() => input.focus({ preventScroll: true }), 50);
        }
    });
    window.addEventListener('hashchange', () => setView(fromHash()));
})();
(() => {
    if (window.__tcCollectionFilters)
        return;
    window.__tcCollectionFilters = true;
    const root = document.getElementById('tcCollectionFilters');
    const grid = document.querySelector('[data-tc-collection-grid]');
    if (!root || !grid)
        return;

    // Local i18n helpers (this IIFE is isolated from the main theme init scope)
    const i18nStr = (key, fallback = '') => {
        try {
            const dict = window.tcI18n || {};
            const v = (dict[key] !== undefined && dict[key] !== null) ? dict[key] : fallback;
            return (v === undefined || v === null) ? '' : v.toString();
        }
        catch (e) {
            return (fallback || '').toString();
        }
    };
    const i18nTpl = (key, fallback, params) => {
        let s = i18nStr(key, fallback);
        if (!params)
            return s;
        Object.keys(params).forEach((k) => {
            const v = (params[k] === undefined || params[k] === null) ? '' : String(params[k]);
            s = s.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'g'), v);
        });
        return s;
    };
    const pager = document.querySelector('[data-tc-client-pager]');
    const prevBtn = pager ? pager.querySelector('[data-tc-pager-prev]') : null;
    const nextBtn = pager ? pager.querySelector('[data-tc-pager-next]') : null;
    const statusEl = pager ? pager.querySelector('[data-tc-pager-status]') : null;
    const perPage = Math.max(1, parseInt(grid.getAttribute('data-tc-per-page') || '36', 10) || 36);
    const btns = Array.from(root.querySelectorAll('[data-tc-filter="brand"], [data-tc-filter-btn="brand"]'));
    const clearBtn = root.querySelector('[data-tc-filter-clear]');
    const countEl = root.querySelector('[data-tc-filter-count]');
    const searchInput = root.querySelector('[data-tc-filter-search]');
    const sortForm = root.querySelector('form.tc-collectionFilters__group--sort');
    const norm = (s) => (s || '').toString().toLowerCase().trim();
    const normSearch = (s) => {
        const str = (s || '').toString().toLowerCase();
        try {
            return str.normalize('NFKD').replace(/[^\p{L}\p{N}]+/gu, '');
        }
        catch (e) {
            return str.replace(/[^a-z0-9]+/g, '');
        }
    };
    const normalizeBrand = (v) => {
        const t = norm(v);
        if (!t)
            return '';
        if (t.includes('tck'))
            return 'knorr';
        if (t.includes('tcw'))
            return 'wabco';
        if (t.includes('tcm'))
            return 'meritor';
        if (t.includes('knorr'))
            return 'knorr';
        if (t.includes('wabco'))
            return 'wabco';
        if (t.includes('meritor'))
            return 'meritor';
        return '';
    };
    const cards = () => Array.from(grid.querySelectorAll('[data-tc-card], .product-card'));
    let emptyEl = document.querySelector('[data-tc-filter-empty]');
    if (!emptyEl) {
        emptyEl = document.createElement('div');
        emptyEl.className = 'tc-filterEmpty';
        emptyEl.setAttribute('data-tc-filter-empty', '');
        emptyEl.hidden = true;
        const emptyTitle = i18nStr('filtersEmptyTitle', 'No products match your filters.');
        const emptyText = i18nStr('filtersEmptyText', 'Try another combination.');
        emptyEl.innerHTML = `<strong>${emptyTitle}</strong> ${emptyText}`;
        grid.insertAdjacentElement('afterend', emptyEl);
    }
    const hydrate = (card) => {
        if (card.dataset.tcHydrated === '1')
            return;
        const title = (card.getAttribute('data-title') || '').trim();
        const vendor = card.getAttribute('data-vendor') || '';
        const tags = card.getAttribute('data-tags') || '';
        const codeEl = card.querySelector ? card.querySelector('[data-tc-code]') : null;
        const code = (codeEl && (codeEl.dataset.tcCodeValue || codeEl.textContent)) ? (codeEl.dataset.tcCodeValue || codeEl.textContent || '') : '';
        const explicitBrand = card.getAttribute('data-tc-brand') || card.dataset.tcBrand || '';
        card.dataset.tcBrand = normalizeBrand(explicitBrand) || normalizeBrand(vendor) || normalizeBrand(tags) || normalizeBrand(title);
        card.dataset.tcHydraulic = card.getAttribute('data-tc-hydraulic') || card.dataset.tcHydraulic || '0';
        // Make search forgiving: ignore punctuation/spaces, and include extracted part code.
        card.dataset.tcSearch = normSearch(`${title} ${vendor} ${tags} ${code}`);
        card.dataset.tcHydrated = '1';
    };
    const readState = () => {
        try {
            const u = new URL(window.location.href);
            return {
                brand: norm(u.searchParams.get('tc_brand') || 'all') || 'all',
                q: normSearch(u.searchParams.get('tc_q') || ''),
                page: Math.max(1, parseInt(u.searchParams.get('tc_page') || '1', 10) || 1)
            };
        }
        catch (e) {
            return { brand: 'all', q: '', page: 1 };
        }
    };
    const writeState = (state, mode = 'replace') => {
        try {
            const u = new URL(window.location.href);
            if (state.brand && state.brand !== 'all')
                u.searchParams.set('tc_brand', state.brand);
            else
                u.searchParams.delete('tc_brand');
            if (state.q)
                u.searchParams.set('tc_q', state.q);
            else
                u.searchParams.delete('tc_q');
            if (state.page && state.page > 1)
                u.searchParams.set('tc_page', String(state.page));
            else
                u.searchParams.delete('tc_page');
            u.searchParams.delete('page');
            if (mode === 'push') {
                window.history.pushState({}, '', u.toString());
            }
            else {
                window.history.replaceState({}, '', u.toString());
            }
        }
        catch (e) { }
    };
    const setActive = (brand) => {
        btns.forEach((b) => {
            const v = norm(b.getAttribute('data-tc-value') || b.getAttribute('data-value') || 'all') || 'all';
            const on = v === brand;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-pressed', String(on));
        });
    };
    const state = readState();
    if (searchInput)
        searchInput.value = (u => {
            // Keep the raw value readable if it came from URL. If we can't, just show it as-is.
            try {
                const url = new URL(window.location.href);
                return (url.searchParams.get('tc_q') || '').toString();
            }
            catch (e) {
                return (state.q || '').toString();
            }
        })();
    setActive(state.brand);

    const scrollToResultsTop = () => {
        // Client-side paging should feel like a normal page load.
        // We use a smooth scroll (per request) but still guard against browsers cancelling the scroll
        // due to focus changes, scroll restoration, or layout shifts.
        try {
            const ae = document.activeElement;
            if (ae && typeof ae.blur === 'function')
                ae.blur();
            try {
                if ('scrollRestoration' in history)
                    history.scrollRestoration = 'manual';
            } catch (e) { }

            const prefersReduced = (() => {
                try {
                    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
                } catch (e) {
                    return false;
                }
            })();

            const getY = () => (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0);
            const setY = (y) => {
                try {
                    const el = document.scrollingElement || document.documentElement;
                    if (el)
                        el.scrollTop = y;
                    if (document.body)
                        document.body.scrollTop = y;
                } catch (e) { }
                window.scrollTo(0, y);
            };

            const startY = getY();
            if (startY <= 2) {
                setY(0);
                return;
            }
            if (prefersReduced) {
                setY(0);
                return;
            }

            // Smooth, deterministic animation (more reliable than native smooth scroll in some browsers)
            const duration = 420;
            const startT = (window.performance && performance.now) ? performance.now() : Date.now();
            const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

            let raf = 0;
            const step = (now) => {
                const curT = (window.performance && performance.now) ? now : Date.now();
                const p = Math.min(1, (curT - startT) / duration);
                const y = Math.round(startY * (1 - easeOutCubic(p)));
                setY(y);
                if (p < 1 && getY() > 0) {
                    raf = requestAnimationFrame(step);
                } else {
                    setY(0);
                }
            };
            raf = requestAnimationFrame(step);

            // Extra guards in case a layout shift tries to pull the scroll back down.
            setTimeout(() => setY(0), 520);
            setTimeout(() => setY(0), 820);
        } catch (e) {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
    };
    const updatePager = (page, totalPages) => {
        if (!pager || !statusEl)
            return;
        const safeTotal = Math.max(1, totalPages || 1);
        const safePage = Math.min(Math.max(1, page || 1), safeTotal);
        statusEl.textContent = `${safePage} / ${safeTotal}`;
        const onePage = safeTotal <= 1;
        if (prevBtn) {
            prevBtn.hidden = onePage;
            prevBtn.disabled = onePage || safePage <= 1;
            prevBtn.setAttribute('aria-disabled', String(prevBtn.disabled));
        }
        if (nextBtn) {
            nextBtn.hidden = onePage;
            nextBtn.disabled = onePage || safePage >= safeTotal;
            nextBtn.setAttribute('aria-disabled', String(nextBtn.disabled));
        }
    };
    const apply = (opts = {}) => {
        const list = cards();
        const brand = state.brand || 'all';
        const q = state.q || '';
        const matched = [];
        for (const card of list) {
            hydrate(card);
            let okBrand = true;
            if (brand === 'all') {
                okBrand = true;
            }
            else if (brand === 'hydraulic') {
                okBrand = (card.dataset.tcHydraulic || card.getAttribute('data-tc-hydraulic') || '0') === '1';
            }
            else {
                okBrand = card.dataset.tcBrand === brand;
            }
            const okSearch = q ? (card.dataset.tcSearch || '').includes(q) : true;
            const ok = okBrand && okSearch;
            card.classList.toggle('tc-isHidden', !ok);
            if (!ok) {
                card.classList.remove('tc-isPageHidden');
                continue;
            }
            matched.push(card);
        }

        const totalPages = Math.max(1, Math.ceil(matched.length / perPage) || 1);
        const clampedPage = Math.min(Math.max(1, state.page || 1), totalPages);
        state.page = clampedPage;

        const start = (clampedPage - 1) * perPage;
        const end = start + perPage;
        matched.forEach((card, idx) => {
            card.classList.toggle('tc-isPageHidden', idx < start || idx >= end);
        });

        const visibleOnPage = matched.length ? Math.min(perPage, matched.length - start) : 0;
        if (countEl)
            countEl.textContent = list.length
                ? i18nTpl('filtersShowing', 'Showing {{ shown }} of {{ total }}', { shown: visibleOnPage, total: matched.length })
                : '';
        if (emptyEl)
            emptyEl.hidden = matched.length !== 0;
        updatePager(clampedPage, totalPages);

        // Ensure cards revealed via client-side pagination/filters are not stuck in the initial
        // tc-reveal hidden state (opacity: 0). This prevents an edge-case where cards become
        // invisible yet still clickable after switching page or applying manufacturer filters.
        try {
          const revealNow = matched.slice(start, end);
          requestAnimationFrame(() => {
            revealNow.forEach((card, i) => {
              if (!card) return;
              if (card.classList.contains('tc-isHidden') || card.classList.contains('tc-isPageHidden')) return;
              if (!card.classList.contains('tc-reveal')) card.classList.add('tc-reveal');
              card.style.setProperty('--tc-reveal-delay', `${(i % 7) * 55}ms`);
              if (!card.classList.contains('is-visible')) {
                card.getBoundingClientRect();
                card.classList.add('is-visible');
              }
            });
          });
        } catch (e) {}
        if (opts.writeUrl !== false)
            writeState(state, opts.writeMode || 'replace');
    };
    const setState = (next, opts = {}) => {
        state.brand = next.brand ?? state.brand ?? 'all';
        state.q = next.q ?? state.q ?? '';
        state.page = next.page ?? state.page ?? 1;
        setActive(state.brand);
        apply(opts);
    };
    root.addEventListener('click', (e) => {
        const pill = e.target.closest('[data-tc-filter="brand"], [data-tc-filter-btn="brand"]');
        if (pill) {
            e.preventDefault();
            setState({
                brand: norm(pill.getAttribute('data-tc-value') || pill.getAttribute('data-value') || 'all') || 'all',
                page: 1
            }, { writeMode: 'push' });
            return;
        }
        const searchBtn = e.target.closest('.tc-collectionFilters__searchBtn');
        if (searchBtn && searchInput) {
            e.preventDefault();
            const raw = (searchInput.value || '').toString();
            const q = normSearch(raw);
            if (q) {
                setState({ q, page: 1 }, { writeMode: 'push' });
            }
            searchInput.focus({ preventScroll: true });
        }
    });
    clearBtn && clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        setState({ brand: 'all', q: '', page: 1 }, { writeMode: 'push' });
        if (searchInput)
            searchInput.value = '';
    });
    if (searchInput) {
        let raf = 0;
        searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                setState({ q: normSearch(searchInput.value), page: 1 }, { writeMode: 'push' });
            }
        });
        searchInput.addEventListener('input', () => {
            if (raf)
                cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                setState({ q: normSearch(searchInput.value), page: 1 }, { writeMode: 'replace' });
            });
        });
    }

    // Support browser back/forward
    window.addEventListener('popstate', () => {
        const s = readState();
        state.brand = s.brand || 'all';
        state.q = s.q || '';
        state.page = s.page || 1;
        if (searchInput)
            searchInput.value = state.q || '';
        setActive(state.brand);
        apply({ writeUrl: false });
    });

    // Client-side pager (Products page)
    if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
            e.preventDefault();
            setState({ page: Math.max(1, (state.page || 1) - 1) }, { writeMode: 'push' });
            scrollToResultsTop();
        });
    }
    if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            setState({ page: (state.page || 1) + 1 }, { writeMode: 'push' });
            scrollToResultsTop();
        });
    }
    if (sortForm) {
        sortForm.addEventListener('submit', () => {
            const upsert = (name, value) => {
                const existing = sortForm.querySelector(`input[name="${name}"]`);
                if (!value || value === 'all') {
                    existing && existing.remove();
                    return;
                }
                let input = existing;
                if (!input) {
                    input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = name;
                    sortForm.appendChild(input);
                }
                input.value = value;
            };
            upsert('tc_brand', state.brand);
            upsert('tc_q', state.q);
        });
    }
    const autoloadAll = grid.getAttribute('data-tc-autoload-all') === '1';
    const loadAllPages = async () => {
        if (!autoloadAll)
            return;
        if (grid.dataset.tcAllLoaded === '1')
            return;
        const total = parseInt(grid.getAttribute('data-tc-total-pages') || '1', 10);
        const current = parseInt(grid.getAttribute('data-tc-current-page') || '1', 10);
        const baseUrl = grid.getAttribute('data-tc-collection-url') || window.location.pathname;
        if (!total || total <= 1)
            return;
        grid.dataset.tcAllLoaded = 'loading';
        const base = new URL(baseUrl, window.location.origin);
        const params = new URLSearchParams(window.location.search);
        params.set('view', 'tc-cards');
        const fetchCards = async (page) => {
            params.set('page', String(page));
            base.search = params.toString();
            const res = await fetch(base.toString(), { credentials: 'same-origin' });
            if (!res.ok)
                return [];
            const html = await res.text();
            if (!html || !html.trim())
                return [];
            const tmp = document.createElement('div');
            tmp.innerHTML = html;
            return Array.from(tmp.querySelectorAll('[data-tc-card], .product-card'));
        };

        // Load pages before current and prepend in correct order
        if (current > 1) {
            const beforeFrag = document.createDocumentFragment();
            for (let page = 1; page < current; page += 1) {
                try {
                    const newCards = await fetchCards(page);
                    if (!newCards.length)
                        break;
                    newCards.forEach((c) => beforeFrag.appendChild(c));
                }
                catch (e) {
                    break;
                }
            }
            if (beforeFrag.childNodes.length) {
                grid.insertBefore(beforeFrag, grid.firstChild);
            }
        }

        // Load pages after current and append
        for (let page = current + 1; page <= total; page += 1) {
            try {
                const newCards = await fetchCards(page);
                if (!newCards.length)
                    break;
                newCards.forEach((c) => grid.appendChild(c));
            }
            catch (e) {
                break;
            }
        }
        grid.dataset.tcAllLoaded = '1';
        apply({ writeUrl: false });
    };
    const mo = new MutationObserver(() => apply({ writeUrl: false }));
    mo.observe(grid, { childList: true, subtree: false });
    setTimeout(() => { apply({ writeUrl: false }); }, 0);
    setTimeout(() => { loadAllPages(); }, 0);
})();
// Premium Specifications accordion: preview + milky blur + accessible toggle
(function () {
  function parsePx(v) {
    var n = parseFloat((v || '').toString());
    return isNaN(n) ? null : n;
  }

  function initTcSpecsPreview() {
    document.querySelectorAll('details.tc-accordion--specs').forEach(function (details) {
      var summary = details.querySelector('summary');
      var body = details.querySelector('.tc-accordion__body');
      if (!summary || !body) return;

      var fade = details.querySelector('.js-tcSpecFade');

      // Ensure the content is measurable/visible so the preview state can work reliably.
      if (!details.hasAttribute('open')) details.setAttribute('open', '');

      function getPreviewMaxPx() {
        var v = getComputedStyle(details).getPropertyValue('--tc-specs-preview-max');
        return parsePx(v) || 380;
      }

      function updateFixedPreviewMax() {
        // Desktop-only: align the Specifications card bottom with the (collapsed) Description card bottom.
        try {
          if (!window.matchMedia('(min-width: 861px)').matches) {
            details.style.removeProperty('--tc-specs-preview-max');
            details.removeAttribute('data-tc-fixed-preview');
            return;
          }

          var layout = details.closest('.tc-productLayout');
          if (!layout) return;

          var desc = layout.querySelector('details.tc-productDesc');
          if (!desc) return;

          // Measure against the collapsed description (summary-only) WITHOUT toggling <details>
          // (avoids flicker and keeps the "base alignment" stable).
          var descRect = desc.getBoundingClientRect();
          var descSum = desc.querySelector('summary');
          var descSumRect = descSum ? descSum.getBoundingClientRect() : null;
          var descStyle = getComputedStyle(desc);
          var bt = parseFloat(descStyle.borderTopWidth || '0') || 0;
          var bb = parseFloat(descStyle.borderBottomWidth || '0') || 0;
          var collapsedBottom = descSumRect ? (descRect.top + descSumRect.height + bt + bb) : descRect.bottom;

          var specsRect = details.getBoundingClientRect();
          var sumRect = summary.getBoundingClientRect();

          var targetTotal = collapsedBottom - specsRect.top;
          if (!isFinite(targetTotal) || targetTotal < 220) return;

          // We set --tc-specs-preview-max for the BODY only.
          // No "magic" offsets: we want the visible bottom edge to match exactly.
          var bodyMax = Math.floor(targetTotal - sumRect.height);
          bodyMax = Math.max(240, bodyMax);

          details.style.setProperty('--tc-specs-preview-max', bodyMax + 'px');
          details.setAttribute('data-tc-fixed-preview', '1');
        } catch (e) {}
      }
      function updateNeedsPreview() {
        // If content is short, skip the preview/blur UX to keep the page clean.
        var maxPx = getPreviewMaxPx();
        var fixed = details.hasAttribute('data-tc-fixed-preview');
        var showFade = body.scrollHeight > (maxPx + 40);
        var needs = fixed ? true : showFade;

        if (!needs) {
          details.classList.remove('is-preview');
          details.classList.add('tc-specs--noPreview');
          if (fade) fade.style.display = 'none';
          summary.setAttribute('aria-expanded', 'true');
          return;
        }

        details.classList.remove('tc-specs--noPreview');
        // Keep preview as the default state unless the user already expanded it.
        if (!details.dataset.tcSpecsTouched) {
          details.classList.add('is-preview');
          summary.setAttribute('aria-expanded', 'false');
        }
        if (fade) fade.style.display = showFade ? '' : 'none';
      }

      function expand() {
        details.dataset.tcSpecsTouched = '1';
        details.classList.remove('is-preview');
        summary.setAttribute('aria-expanded', 'true');
      }

      function collapse() {
        details.dataset.tcSpecsTouched = '1';
        details.classList.add('is-preview');
        summary.setAttribute('aria-expanded', 'false');
      }

      function toggle() {
        if (details.classList.contains('is-preview')) expand();
        else collapse();
      }


      // Initial calculation (sync) to avoid first-paint misalignment.
      updateFixedPreviewMax();
      updateNeedsPreview();

      // Recalc helper (rAF-throttled) so the bases stay aligned even if media/fonts/layout shift.
      var __tcRaf = 0;
      function scheduleRecalc() {
        if (__tcRaf) cancelAnimationFrame(__tcRaf);
        __tcRaf = requestAnimationFrame(function () {
          __tcRaf = 0;
          updateFixedPreviewMax();
          updateNeedsPreview();
        });
      }


      // Run a short stabilization loop to catch late layout shifts (fonts, dynamic price, injected content).
      function stabilizeRecalc() {
        var i = 0;
        var maxI = 26; // ~2s at 80ms
        (function tick(){
          scheduleRecalc();
          i += 1;
          if (i < maxI) setTimeout(tick, 80);
        })();
      }
      // Toggle without letting <details> fully collapse (we want preview, not empty).
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (!details.hasAttribute('open')) details.setAttribute('open', '');
        toggle();
        scheduleRecalc();
      });

      if (fade) {
        function openByFade() {
          expand();
          summary.focus({ preventScroll: true });
        }

        fade.addEventListener('click', openByFade);
        fade.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openByFade();
          }
        });
      }

      // Initial + responsive recalculation
      setTimeout(function () {
        stabilizeRecalc();
      }, 0);

      // Re-run after images/fonts settle (product gallery affects the alignment target).
      window.addEventListener('load', function () {
        setTimeout(function () {
          stabilizeRecalc();
        }, 0);
        setTimeout(function () {
          stabilizeRecalc();
        }, 420);
      });
      if (document.fonts && document.fonts.ready) {
        try {
          document.fonts.ready.then(function () {
            setTimeout(function () { stabilizeRecalc(); }, 0);
          });
        } catch (e) {}
      }

      window.addEventListener('resize', function () {
        clearTimeout(details.__tcSpecsResizeT);
        details.__tcSpecsResizeT = setTimeout(function () {
          scheduleRecalc();
        }, 80);
      });

      // Keep the alignment stable if the left column (media) or description block changes height.
      if (typeof ResizeObserver !== 'undefined') {
        try {
          if (!details.__tcSpecsRO) {
            var ro = new ResizeObserver(function () {
              scheduleRecalc();
            });
            details.__tcSpecsRO = ro;

            var layoutEl = details.closest('.tc-productLayout');
            if (layoutEl) ro.observe(layoutEl);

            var descEl = layoutEl ? layoutEl.querySelector('details.tc-productDesc') : null;
            if (descEl) ro.observe(descEl);

            var mediaEl = layoutEl ? layoutEl.querySelector('[data-tc-product-gallery], .tc-productGallery, .tc-productGallery__main') : null;
            if (mediaEl) ro.observe(mediaEl);

            ro.observe(details);

            // Watch for DOM injections/changes that can shift positions without changing box sizes.
            if (!details.__tcSpecsMO && typeof MutationObserver !== 'undefined') {
              try {
                var mo2 = new MutationObserver(function () {
                  scheduleRecalc();
                });
                details.__tcSpecsMO = mo2;
                var watchEl = layoutEl ? (layoutEl.querySelector('.tc-productRightStack') || layoutEl.querySelector('.tc-productSubRow') || layoutEl) : details;
                mo2.observe(watchEl, { childList: true, subtree: true, attributes: true });
              } catch (e) {}
            }
          }
        } catch (e) {}
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTcSpecsPreview);
  } else {
    initTcSpecsPreview();
  }
})();
;


/* =========================================================
   TC: Specifications horizontal navigation (native scroll)
   - Uses real scrollbar (requested)
   - Left/right buttons scroll the container (no transform panning)
   ========================================================= */

(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function initTcSpecsXNavNative() {
    const specsSections = document.querySelectorAll('details.tc-accordion--specs[data-tc-specs-v2]');
    if (!specsSections || specsSections.length === 0) return;

    specsSections.forEach((details) => {
      const scrollContainer = details.querySelector('.tc-specsV2__xscroll');
      const navContainer = details.querySelector('[data-tc-specs-xnav]');
      if (!scrollContainer || !navContainer) return;

      const leftButton = navContainer.querySelector('[data-tc-specs-xbtn="left"]');
      const rightButton = navContainer.querySelector('[data-tc-specs-xbtn="right"]');
      if (!leftButton || !rightButton) return;

      if (details.__tcSpecsXNavNativeInit) return;
      details.__tcSpecsXNavNativeInit = true;

      function getStep() {
        return Math.max(260, Math.floor((scrollContainer.clientWidth || 0) * 0.75));
      }

      function update() {
        const maxScroll = Math.max(0, scrollContainer.scrollWidth - scrollContainer.clientWidth);
        const x = scrollContainer.scrollLeft;
        leftButton.disabled = x <= 2;
        rightButton.disabled = x >= (maxScroll - 2);
      }

      function scrollBy(delta) {
        try {
          scrollContainer.scrollBy({ left: delta, behavior: 'smooth' });
        } catch (e) {
          // Safari fallback
          scrollContainer.scrollLeft = Math.max(0, scrollContainer.scrollLeft + delta);
        }
      }

      leftButton.addEventListener('click', () => scrollBy(-getStep()));
      rightButton.addEventListener('click', () => scrollBy(getStep()));

      scrollContainer.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });

      // Initial update (including after async layout changes)
      update();
      setTimeout(update, 250);
      setTimeout(update, 900);
    });
  }

  ready(initTcSpecsXNavNative);
  document.addEventListener('shopify:section:load', initTcSpecsXNavNative);
  document.addEventListener('shopify:section:select', initTcSpecsXNavNative);
})();

;



